<!-- eslint-disable max-len -->
<template>
  <div
    v-if="dataLoaded"
    class="min-h-full"
  >
    <!-- Top navbar — always present so the seller can reach profile / logout
         without having to leave the plugin dashboard. Extra important for
         single-plugin sellers whose "dashboard" IS this view. -->
    <div class="border-b border-gray-200 bg-white">
      <div class="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
        <router-link
          :to="{ name: 'DashboardPage' }"
          class="inline-flex items-center"
        >
          <img
            class="h-8 w-auto"
            src="@/assets/logo2.png"
            alt="LiveLink"
          >
        </router-link>
        <TopUserMenu />
      </div>
    </div>

    <!-- Connection health. Sits above the page content on every screen
         under this layout (painel, bio, automações). The dashboard is the
         landing screen, so it also opts into the one-per-session modal. -->
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4 mb-4">
      <PluginConnectionAlert
        :plugin="plugin"
        :show-modal="true"
      />
    </div>

    <div class="mx-auto">
      <RouterView
        :plugin="plugin"
        :has-multiple-plugins="hasMultiplePlugins"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  computed,
  onBeforeMount,
  onMounted,
} from 'vue';
import { useRouter } from 'vue-router';
import usePluginStore from '@/store/plugin/plugin';
import Plugin from '@/models/plugin/plugin';
import TopUserMenu from '@/components/tailwind/layout/shared/TopUserMenu.vue';
import PluginConnectionAlert from '@/components/tailwind/plugin/PluginConnectionAlert.vue';

const router = useRouter();
const { pluginsDoGetMy } = usePluginStore();

/*
  Data
*/
const dataLoaded = ref(false);
const plugin = ref({} as Plugin);
const newPlugins = ref([] as Plugin[]);
// True when the seller has more than one IG/FB plugin — child views use it
// to decide whether to show the "← Painel" back button (a 1-plugin seller
// would just bounce right back here via the auto-redirect).
const hasMultiplePlugins = ref(false);

/*
  Computed
*/
const pluginId = computed(() => router.currentRoute.value.params.pluginId);
const pluginType = computed(() => router.currentRoute.value.params.pluginType);

/*
  Methods
*/
const redirectDashboard = ((error: string) => router.push({
  name: 'DashboardPage',
  query: {
    message: 'Não foi possível carregar a página. Tente novamente em instantes.',
    detail: error,
    type: 'error',
  },
}));

const loadPlugins = () => pluginsDoGetMy(`${pluginId.value}`, undefined, `${pluginType.value}`)
  .then((response) => {
    if (response !== undefined) {
      const plugins = pluginType.value === 'facebook'
        ? response.facebookPlugins
        : response.instagramPlugins;
      if (plugins.length > 0) {
        plugin.value = new Plugin(plugins[0]);
      }
    }
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('error', error);
    redirectDashboard(error.toString());
  });

// Second unfiltered call — counts every IG/FB plugin the user has so the
// child view can hide navigation that would just bounce them back here.
// Small extra call; runs in parallel with `loadPlugins`.
const loadPluginCount = () => pluginsDoGetMy()
  .then((response) => {
    if (response === undefined) return;
    const igCount = response.instagramPlugins?.length || 0;
    const fbCount = response.facebookPlugins?.length || 0;
    hasMultiplePlugins.value = igCount + fbCount > 1;
  })
  .catch(() => {
    // Non-critical — default to false (single-plugin) if count fails. Worst
    // case the back button is hidden when it could have been shown; the user
    // still has TopUserMenu + logo to navigate.
  });

const load = async () => {
  await Promise.all([loadPlugins(), loadPluginCount()]);
  dataLoaded.value = true;
};

/*
  Hooks
*/
onBeforeMount(async () => {
  await load();
});
onMounted(() => {
  setTimeout(() => {
    dataLoaded.value = true;
  }, 500);
});
watch(newPlugins, () => {
  load();
});
</script>
